import { Box, Typography, styled } from '@mui/material';
import { ThemeTypography, Skeleton } from 'DesignSystem';
import React, { FC } from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import {
  ImpactCatalogItem,
  ImpactClaimStatus,
} from 'FeatureModules/Request/types/impactClaim.types';
import { getStatusSubtitle } from 'FeatureModules/ImpactClaim/utils/impactClaim.utils';
import { impactCategoryConfig } from 'FeatureModules/ImpactClaim/utils/impactCategoryConfig';

interface IClaimTitleAndStatusProps {
  impactCatalogItem: ImpactCatalogItem;
  loading?: boolean;
  transparent?: boolean;
  claimStatus: ImpactClaimStatus | 'GROUP';
  showSeeComments?: boolean;
  enlargeTitle?: boolean;
}

const OpacityContainer = styled('div')<{ transparent: Tbooleanish }>(({ transparent }) => ({
  opacity: transparent === 'true' ? 0.5 : 1,
}));

const SubTitle = styled(Typography)<{ status: ImpactClaimStatus | 'GROUP' }>(
  ({ theme, status }) => ({
    fontSize: 13,

    color:
      status === 'APPROVED'
        ? theme.palette.success.main
        : status === 'DENIED'
        ? theme.palette.error.main
        : theme.custom.colors.textLight,
    fontWeight: 500,
  })
);

const CategoryIcon = styled('img')(() => ({
  position: 'absolute',
  bottom: -2,
  left: -2,
  width: 50,
}));

const ClaimIcon = styled('img')(() => ({
  width: 40,
  height: 'auto',
}));

const ClaimTitleAndStatus: FC<IClaimTitleAndStatusProps> = ({
  impactCatalogItem,
  loading,
  transparent,
  claimStatus,
  showSeeComments,
  enlargeTitle = false,
}) => {
  // Determined either by the parent or by the claim status
  const shouldBeTransparent =
    transparent !== undefined
      ? transparent
      : ['ARCHIVED', 'CREATED', 'UPDATED', 'REQUESTED', 'GROUP'].includes(claimStatus);

  const statusAddition = showSeeComments && claimStatus === 'DENIED' ? ' - SEE COMMENT' : null;

  const fallbackIcon = impactCategoryConfig[impactCatalogItem?.category.type]?.icon;

  return (
    <OpacityContainer transparent={Booleanish(shouldBeTransparent)}>
      <Box display="flex">
        {impactCatalogItem?.iconUrl ? (
          <Box
            height={45}
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="absolute"
          >
            <ClaimIcon src={impactCatalogItem?.iconUrl} alt="claim icon" />
          </Box>
        ) : (
          <CategoryIcon src={fallbackIcon} alt="claim icon" />
        )}
        <Box pl={7}>
          <ThemeTypography variant={enlargeTitle ? 'TITLE_SMALL' : 'ITEM_TITLE'}>
            {loading ? <Skeleton variant="text" width={150} /> : impactCatalogItem?.title}
          </ThemeTypography>
          <SubTitle status={claimStatus} data-testid="claim-status">
            {loading ? <Skeleton variant="text" width={70} /> : getStatusSubtitle(claimStatus)}
            {statusAddition}
          </SubTitle>
        </Box>
      </Box>
    </OpacityContainer>
  );
};

export default ClaimTitleAndStatus;
